/**
 * Masquerade (admin impersonation) detection for the `/users/me` payload.
 *
 * When an administrator is impersonating another account, the SSO assertion
 * carries the *masqueraded* user as the identity and the real admin separately,
 * so `/users/me` describes the effective user and attaches the admin under a
 * `masquerade` block:
 *
 * ```json
 * "user": {
 *   "firstName": "Alex", "lastName": "Bajoris",
 *   "email": "alex+admin@builtbyworkhorse.com",
 *   "masquerade": {
 *     "active": true, "masquerading": "true",
 *     "admin": "pratiks@mitrmedia.com", "adminId": "208"
 *   }
 * }
 * ```
 *
 * Kept free of i18n and of the store so it can be unit-tested against real
 * payload shapes; the caller supplies the wording for anything missing.
 */
// The payload sends `active` as a real boolean and `masquerading` as the string
// "true" in the same object, and neither type is guaranteed by the SAML claim
// (attribute values arrive as strings). Accept both forms rather than trusting
// one field's type.
var isTrue = function (value) {
    return value === true || (typeof value === 'string' && value.trim().toLowerCase() === 'true');
};
var clean = function (value) {
    if (value === null || value === undefined)
        return null;
    var str = String(value).trim();
    return str === '' ? null : str;
};
/**
 * Returns masquerade details when the payload says a masquerade is active, else
 * null. `active` OR `masquerading` being true is enough: a session where only
 * one of them survives the claim mapping is still an impersonation, and failing
 * closed here would silently hide that fact — the one thing this notice exists
 * to disclose.
 */
export var parseMasquerade = function (meUser) {
    var _a;
    var claims = meUser === null || meUser === void 0 ? void 0 : meUser.masquerade;
    if (!claims)
        return null;
    if (!isTrue(claims.active) && !isTrue(claims.masquerading))
        return null;
    var adminEmail = clean(claims.admin);
    var adminId = clean(claims.adminId);
    var adminLabel = adminEmail !== null && adminEmail !== void 0 ? adminEmail : (adminId ? "#".concat(adminId) : null);
    var name = [clean(meUser === null || meUser === void 0 ? void 0 : meUser.firstName), clean(meUser === null || meUser === void 0 ? void 0 : meUser.lastName)].filter(Boolean).join(' ').trim();
    var contact = (_a = clean(meUser === null || meUser === void 0 ? void 0 : meUser.email)) !== null && _a !== void 0 ? _a : clean(meUser === null || meUser === void 0 ? void 0 : meUser.username);
    var viewingAs = name && contact ? "".concat(name, " (").concat(contact, ")") : name || contact || null;
    // Name first, contact only as a fallback: a display that has room for one
    // identifier should show the human-readable one. Falling back to the email
    // rather than to null keeps the disclosure present for a payload that carries
    // no name at all — an unnamed masquerade is still a masquerade.
    var viewingAsName = name || contact || null;
    return { adminLabel: adminLabel, viewingAs: viewingAs, viewingAsName: viewingAsName };
};
